<template>
  <main class="main">
    <div class="anim-wrap">
      <div class="container">
        <div class="row">
          <div class="col-md-5">
            <div class="sheet-panel" v-if="transformedScores">
              <div class="sheet-header">
                <h3>RIASEC distribution</h3>

                <button
                  class="info-tip"
                  title="Below is your RIASEC score split across 6 interest areas"
                  v-tippy="{ theme: 'navy' }"
                >
                  <font-awesome-icon icon="info" />
                </button>
                <router-link
                  to="/test/pathways"
                  class="icon-link card-link"
                  title="Re-take test"
                  v-tippy
                >
                  <font-awesome-icon icon="redo" />
                </router-link>
              </div>
              <div class="sheet-content">
                <div class="riasec-chart">
                  <div
                    v-for="item in transformedScores.scores"
                    v-bind:key="item.area"
                    class="band"
                    :class="slugifyString(item.area)"
                    v-tippy="{ theme: 'navy' }"
                    :title="item.area"
                  >
                    <div class="bar">
                      <div
                        class="fill"
                        v-bind:style="{
                          height:
                            (item.score / transformedScores.maxValue) * 100 +
                            '%',
                        }"
                      ></div>
                    </div>
                    <span class="letter">{{ truncateString(item.area) }}</span>
                  </div>
                </div>
                <p style="padding: 15px;">
                  Holland Occupational Themes (RIASEC) is a vocational theory
                  based on individual interests organized as Realistic (Doers),
                  Investigative (Thinkers), Artistic (Creators), Social
                  (Helpers), Enterprising (Persuaders), and Conventional
                  (Organizers).
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-7">
            <div class="sheet-panel" v-if="transformedScores">
              <div class="sheet-header">
                <h3>Interest code</h3>
                <button
                  class="info-tip"
                  title="Below is your top RIASEC interest areas"
                  v-tippy="{ theme: 'navy' }"
                >
                  <font-awesome-icon icon="info" />
                </button>
              </div>
              <div class="sheet-content">
                <div
                  v-for="item in orderedScores"
                  v-bind:key="item.area"
                  class="riasec-highlight"
                  :class="slugifyString(item.area)"
                >
                  <div class="band">
                    <div class="hexa">
                      <svg
                        width="53"
                        height="49"
                        viewBox="0 0 53 49"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M51.4019 20C53.0096 22.7846 53.0096 26.2154 51.4019 29L42.8481 43.8157C41.2404 46.6003 38.2692 48.3157 35.0538 48.3157L17.9462 48.3157C14.7308 48.3157 11.7596 46.6003 10.1519 43.8157L1.59808 29C-0.00961813 26.2154 -0.00961798 22.7846 1.59808 20L10.1519 5.1843C11.7596 2.39969 14.7308 0.6843 17.9462 0.6843L35.0538 0.684301C38.2692 0.684301 41.2404 2.39969 42.8481 5.1843L51.4019 20Z"
                        />
                      </svg>
                      <div class="letter">{{ truncateString(item.area) }}</div>
                    </div>
                  </div>
                  <div class="description">
                    <h3>{{ item.area }}</h3>
                    <p>{{ item.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <div class="sheet-panel" v-if="careerMatches">
              <div class="sheet-header">
                <h3>Job matches</h3>
                <button
                  class="info-tip"
                  title="Jobs listed underneath have a good match with your RIASEC interest profile"
                  v-tippy="{ theme: 'navy' }"
                >
                  <font-awesome-icon icon="info" />
                </button>
              </div>
              <div class="sheet-content">
                <div class="row">
                  <div class="job-list col-md-6">
                    <div
                      v-for="item in careerMatches"
                      v-bind:key="item.code"
                      class="list-item"
                      :class="{ active: item.code === activeCareer }"
                      @click="toggleCareer(item.code)"
                    >
                      <div class="icon-holder">
                        <img
                          :src="
                            'https://cdn.traitstack.com/static/' +
                              item.topcat +
                              '.svg'
                          "
                          class="svg-image"
                          alt
                        />
                      </div>
                      <div class="title-holder">
                        <h3>{{ item.title }}</h3>
                        <span class="subtitle">{{ item.category }}</span>
                      </div>

                      <button
                        v-if="item.tags.bright_outlook"
                        class="bright"
                        title="This job has a bright future"
                        v-tippy="{ theme: 'navy' }"
                      >
                        <font-awesome-icon icon="arrow-up" />
                      </button>

                      <div
                        class="fit"
                        :title="item.fit + ' match'"
                        v-tippy="{ theme: 'navy' }"
                      >
                        <div
                          class="mini-chart"
                          :class="slugifyString(item.fit)"
                        >
                          <span></span>
                        </div>
                        <span class="match">MATCH</span>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="job-information" v-if="selectedCareerData">
                      <button @click="closeModal()" class="close-modal">
                        <font-awesome-icon icon="times" />
                      </button>
                      <div class="header-wrap">
                        <div class="image-wrap">
                          <img
                            :src="
                              'https://cdn.traitstack.com/static/' +
                                transformedCode +
                                '.svg'
                            "
                            class="svg-image"
                            alt
                          />
                        </div>
                        <div class="content-wrap">
                          <a
                            target="_blank"
                            :href="
                              'https://traitstack.com/jobs/' +
                                selectedCareerData.code
                            "
                            ><h3>{{ selectedCareerData.title }}</h3></a
                          >
                          <h4>{{ pathwaysCategories[transformedCode] }}</h4>
                          <h5>
                            Also called:
                            <span
                              v-for="item in selectedCareerData.also_called
                                .title"
                              v-bind:key="item"
                              >{{ item }}</span
                            >
                          </h5>
                        </div>
                      </div>

                      <h4>On the job</h4>
                      <p>{{ selectedCareerData.what_they_do }}</p>
                      <h4>Tasks</h4>
                      <ul>
                        <li
                          v-for="item in selectedCareerData.on_the_job.task"
                          v-bind:key="item"
                        >
                          {{ item }}
                        </li>
                      </ul>
                      <a
                        class="btn btn-primary btn-md"
                        target="_blank"
                        :href="
                          'https://traitstack.com/jobs/' +
                            selectedCareerData.code
                        "
                        >View full job profile
                        <font-awesome-icon icon="external-link-alt"
                      /></a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <div class="sheet-panel" v-if="careerMatches">
              <div class="sheet-content text-center">
                <p>
                  <a
                    href="https://services.onetcenter.org/"
                    title="This site incorporates information from O*NET Web Services. Click to learn more."
                    target="_blank"
                    ><img
                      src="https://www.onetcenter.org/image/link/onet-in-it.svg"
                      style="width: 130px; height: 60px; border: none"
                      alt="O*NET in-it"
                  /></a>
                </p>
                <p>
                  <small
                    >This site incorporates information from
                    <a href="https://services.onetcenter.org/" target="_blank"
                      >O*NET Web Services</a
                    >
                    by the U.S. Department of Labor, Employment and Training
                    Administration (USDOL/ETA). O*NET® is a trademark of
                    USDOL/ETA.</small
                  >
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapState } from "vuex";
import { pathwaysCategories } from "@/assets/pathwaysCategories.js";

export default {
  name: "resultspathways",
  components: {},
  computed: {
    ...mapState(["user"]),
    ...mapState(["userData"]),
    getUser() {
      return this.user ? this.user : "";
    },
    getUserData() {
      return this.userData ? this.userData : "";
    },
    orderedScores() {
      const scores = JSON.parse(JSON.stringify(this.transformedScores.scores));
      scores.sort(function(b, a) {
        return parseFloat(a.score) - parseFloat(b.score);
      });
      return scores.slice(0, 3);
    },
    transformedCode() {
      let cat = this.activeCareer.substring(0, 2) + "-0000";
      return cat;
    },
  },
  data() {
    return {
      givenFeedback: false,
      riasecScore: null,
      careerMatches: null,
      transformedScores: null,
      activeCareer: null,
      selectedCareerData: null,
      pathwaysCategories: pathwaysCategories,
    };
  },
  mounted() {
    let pathwaysScore = this.getUserData.pathways.scores;
    this.axios
      .get(
        "https://services.onetcenter.org/ws/mnm/interestprofiler/results?answers=" +
          pathwaysScore +
          "&client=traitstack"
      )
      .then((response) => {
        this.transformRiasecScores(response.data.result);
      });
    this.axios
      .get(
        "https://services.onetcenter.org/ws/mnm/interestprofiler/careers?start=1&end=99&answers=" +
          pathwaysScore +
          "&client=traitstack"
      )
      .then((response) => {
        this.transformCareerMatches(response.data.career);
      });
  },
  methods: {
    slugifyString(text) {
      text = text.toLowerCase();
      text = text.substring(0, 3);
      return text;
    },
    truncateString(text) {
      text = text.substring(0, 1);
      return text;
    },
    closeModal() {
      this.selectedCareerData = null;
    },
    toggleCareer(code) {
      this.activeCareer = code;
      var item = this.careerMatches.filter((obj) => {
        return obj.code === code;
      });
      if (item[0].data == null) {
        console.log("Data loaded");
        this.axios
          .get(
            "https://services.onetcenter.org/ws/mnm/careers/" +
              code +
              "?client=traitstack"
          )
          .then((response) => {
            item[0].data = response.data;
            this.selectedCareerData = item[0].data;
          });
      } else {
        this.selectedCareerData = item[0].data;
      }
    },
    transformCareerMatches(careerData) {
      const careers = [];
      careerData.forEach((element) => {
        element.data = null;
        let cat = element.code.substring(0, 2);
        element.topcat = cat + "-0000";
        element.category = this.pathwaysCategories[cat + "-0000"];
        careers.push(element);
      });
      this.careerMatches = careers;
      let widthCheck = window.matchMedia("(max-width: 768px)");
      if (!widthCheck.matches) {
        this.toggleCareer(this.careerMatches[0].code);
      }
    },
    transformRiasecScores(scoreData) {
      let maxValue = 0;

      scoreData.forEach((element) => {
        if (element.score > maxValue) {
          maxValue = element.score;
        }
      });

      maxValue = maxValue + 3;

      const scores = {
        maxValue: maxValue,
        scores: scoreData,
      };

      this.transformedScores = scores;
    },
  },
};
</script>
